import React, { useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Slide from "@material-ui/core/Slide";
import { Button, TextField, IconButton } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import styled from "styled-components";
import { Close } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../../services/api";
import { COLORS } from "../../../assets/colors";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles(() => ({
  dialogPaper: {
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 20px 25px -5px rgba(0,0,0,0.15), 0 10px 10px -5px rgba(0,0,0,0.04)",
  },
  textField: {
    marginBottom: "1rem",
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      transition: "all 0.3s ease",
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: COLORS.primaryLight100,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: COLORS.primaryColor,
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-outlined.Mui-focused": {
      color: COLORS.primaryColor,
    },
  },
  textFieldHalf: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      transition: "all 0.3s ease",
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: COLORS.primaryLight100,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: COLORS.primaryColor,
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-outlined.Mui-focused": {
      color: COLORS.primaryColor,
    },
  },
  submitBtn: {
    borderRadius: "20px",
    padding: "8px 24px",
    fontWeight: "bold",
    textTransform: "none",
    boxShadow: "none",
    transition: "all 0.3s ease-in-out",
    backgroundColor: COLORS.primaryColor,
    color: "white",
    "&:hover": {
      backgroundColor: COLORS.primaryLight500,
      transform: "translateY(-1px)",
      boxShadow: "0 4px 12px rgba(5,71,82,0.25)",
    },
  },
  cancelBtn: {
    borderRadius: "20px",
    padding: "8px 20px",
    fontWeight: "600",
    textTransform: "none",
    color: COLORS.secondaryTextColor,
    "&:hover": {
      backgroundColor: "rgba(112, 140, 145, 0.08)",
    },
  },
}));

export default function RideWishModal({
  open,
  handleClose,
  leavingFrom = "",
  goingTo = "",
  seats = 1,
  date = "",
}) {
  const classes = useStyles();
  const [formData, setFormData] = useState({
    leaving_from: leavingFrom,
    heading_to: goingTo,
    seats: seats || 1,
    preferred_date: date || "",
    contact_name: "",
    contact_phone: "",
    note: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!formData.leaving_from || !formData.heading_to) {
      toast.error("Please enter origin and destination");
      return;
    }
    try {
      setLoading(true);
      const response = await api.post("/ride-wishes/create", formData);
      if (response.status === 201) {
        setSubmitted(true);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <StyledDialogTitle>
        <div className="title-content">
          <span className="emoji">🔔</span>
          <span>{submitted ? "Request Submitted!" : "Request a Ride"}</span>
        </div>
        <IconButton size="small" onClick={handleClose}>
          <Close />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        {submitted ? (
          <SuccessState>
            <div className="success-icon">✅</div>
            <h3>Thank you for letting us know!</h3>
            <p>
              We've recorded your ride request. Our drivers and team will see
              this and try to arrange a ride for you.
            </p>
            <p className="sub">
              Keep checking back — new rides are added daily!
            </p>
          </SuccessState>
        ) : (
          <>
            <p className="subtitle">
              Can't find a ride? Tell us what you need and we'll try to match
              you with a driver.
            </p>

            <div className="form-row">
              <TextField
                label="Leaving From *"
                name="leaving_from"
                value={formData.leaving_from}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                size="small"
                className={classes.textFieldHalf}
              />
              <TextField
                label="Going To *"
                name="heading_to"
                value={formData.heading_to}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                size="small"
                className={classes.textFieldHalf}
              />
            </div>

            <div className="form-row">
              <TextField
                label="Preferred Date"
                name="preferred_date"
                type="date"
                value={formData.preferred_date}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                className={classes.textFieldHalf}
              />
              <TextField
                label="Seats Needed"
                name="seats"
                type="number"
                value={formData.seats}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                size="small"
                inputProps={{ min: 1, max: 6 }}
                className={classes.textFieldHalf}
              />
            </div>

            <div className="form-row">
              <TextField
                label="Your Name"
                name="contact_name"
                value={formData.contact_name}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                size="small"
                className={classes.textFieldHalf}
              />
              <TextField
                label="Phone Number"
                name="contact_phone"
                value={formData.contact_phone}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                size="small"
                className={classes.textFieldHalf}
              />
            </div>

            <TextField
              label="Any note for the driver? (optional)"
              name="note"
              value={formData.note}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              size="small"
              multiline
              rows={2}
              className={classes.textField}
            />
          </>
        )}
      </StyledDialogContent>

      <StyledDialogActions>
        {submitted ? (
          <Button onClick={handleClose} variant="contained" className={classes.submitBtn}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} className={classes.cancelBtn}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              className={classes.submitBtn}
            >
              {loading ? "Submitting..." : "Submit Request"}
            </Button>
          </>
        )}
      </StyledDialogActions>
    </Dialog>
  );
}

const StyledDialogTitle = styled(DialogTitle)`
  && {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: ${COLORS.backgroundGradient};
    color: white;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);

    .title-content {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-size: 1.3rem;
      font-weight: 700;
      letter-spacing: 0.5px;

      .emoji {
        font-size: 1.6rem;
        animation: pulse 2s infinite;
      }
    }

    button, .MuiIconButton-root {
      color: rgba(255, 255, 255, 0.85);
      background-color: rgba(255, 255, 255, 0.1);
      transition: all 0.2s ease-in-out;
      &:hover {
        color: white;
        background-color: rgba(255, 255, 255, 0.2);
      }
    }

    @keyframes pulse {
      0% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.15);
      }
      100% {
        transform: scale(1);
      }
    }
  }
`;

const StyledDialogContent = styled(DialogContent)`
  && {
    padding: 2rem 2.25rem 1.5rem;

    .subtitle {
      color: ${COLORS.secondaryTextColor};
      margin-bottom: 1.5rem;
      font-size: 0.95rem;
      line-height: 1.6;
    }

    .form-row {
      display: flex;
      gap: 1.25rem;
      margin-bottom: 1.25rem;

      @media (max-width: 480px) {
        flex-direction: column;
        gap: 1rem;
        margin-bottom: 1rem;
      }
    }
  }
`;

const StyledDialogActions = styled(DialogActions)`
  && {
    padding: 1rem 2.25rem 1.5rem;
    border-top: 1px solid ${COLORS.borderColor};
  }
`;

const SuccessState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2.5rem 1rem;
  animation: scaleIn 0.4s ease-out;

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  .success-icon {
    font-size: 3rem;
    width: 80px;
    height: 80px;
    background: rgba(16, 185, 129, 0.1);
    color: #10B981;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
    box-shadow: 0 0 20px rgba(16, 185, 129, 0.15);
  }

  h3 {
    color: ${COLORS.primaryColor};
    margin-bottom: 0.75rem;
    font-size: 1.4rem;
    font-weight: 700;
  }

  p {
    color: ${COLORS.secondaryTextColor};
    line-height: 1.6;
    max-width: 380px;
    font-size: 0.95rem;
  }

  .sub {
    margin-top: 1rem;
    font-weight: 600;
    color: ${COLORS.primaryLight100};
  }
`;
